/**
 * 관리자 대리인(Agent) 관리 (M13) API wrapper.
 *
 * 백엔드 엔드포인트:
 *   GET    /api/admin/agents?include_inactive=<bool>
 *   GET    /api/admin/agents/{agent_id}
 *   POST   /api/admin/agents
 *   PATCH  /api/admin/agents/{agent_id}
 *   DELETE /api/admin/agents/{agent_id}
 *   GET    /api/admin/agents/rules/export
 *   POST   /api/admin/agents/rules/import/preview
 *   POST   /api/admin/agents/rules/import
 *
 * 모든 엔드포인트는 ``require_admin`` 가드.
 * 79999 (UNKNOWN AGENT) 는 백엔드에서 수정/삭제 시 400 으로 차단된다.
 */

import { apiClient } from "./client";

/** agent.extraction_rules 의 자유 형식 dict. 현재는 dict-of-anything 으로 취급. */
export type AgentExtractionRules = Record<string, unknown>;

/** agent.review_rules 항목 — M23_2 검토 주의사항(soft). shape: {field: {note, reason}}. */
export interface AgentReviewRuleEntry {
  note?: string;
  reason?: string;
}
export type AgentReviewRules = Record<string, AgentReviewRuleEntry>;

export interface AgentOut {
  id: string;
  official_name: string;
  currency: string | null;
  keywords: string[];
  extraction_rules: AgentExtractionRules | null;
  /** M23_2 — 위치규칙으로 환원 불가한 상습오류. 최종 AI 검토에 soft 주입(읽기 전용 표시). */
  review_rules: AgentReviewRules | null;
  priority: number;
  withholding_enabled: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AgentCreatePayload {
  /** 최신 마스터 支払先コード 5자리 (예: "70486"). 백엔드 정규식: ^[A-Za-z0-9_-]+$ */
  id: string;
  official_name: string;
  currency?: string | null;
  /** OCR 매칭 키워드. 백엔드에서 trim + 빈값 제거 + 순서 유지 중복 제거. */
  keywords?: string[];
  extraction_rules?: AgentExtractionRules | null;
  /** 0~10000, 높을수록 우선. 기본 50. */
  priority?: number;
  /** 원천징수세(withholding_tax) 추출 활성화. 기본 false. */
  withholding_enabled?: boolean;
  is_active?: boolean;
}

/**
 * PATCH payload — 모든 필드 optional.
 *
 * 주의: 빈 객체 `{}` 를 보내면 백엔드가 400 ("변경할 필드가 없습니다.")
 * 응답하므로, 호출 측에서 변경된 필드만 추려 보내야 한다.
 */
export interface AgentUpdatePayload {
  official_name?: string;
  currency?: string | null;
  keywords?: string[];
  extraction_rules?: AgentExtractionRules | null;
  /** 교체(merge 아님). 특정 키를 제외한 dict 를 보내면 그 항목만 삭제된다. */
  review_rules?: AgentReviewRules | null;
  priority?: number;
  withholding_enabled?: boolean;
  is_active?: boolean;
}

/** 매칭 실패용 sentinel. 백엔드 정의(`UNKNOWN_AGENT_ID`)와 동기 유지. */
export const UNKNOWN_AGENT_ID = "79999" as const;

export interface ListAgentsParams {
  /** false 면 `is_active=true` 인 agent 만 반환. 기본 true. */
  includeInactive?: boolean;
}

export async function listAgents(params: ListAgentsParams = {}): Promise<AgentOut[]> {
  const { includeInactive = true } = params;
  const res = await apiClient.get<AgentOut[]>("/admin/agents", {
    params: { include_inactive: includeInactive },
  });
  return res.data;
}

export async function getAgent(agentId: string): Promise<AgentOut> {
  const res = await apiClient.get<AgentOut>(`/admin/agents/${encodeURIComponent(agentId)}`);
  return res.data;
}

export async function createAgent(payload: AgentCreatePayload): Promise<AgentOut> {
  const res = await apiClient.post<AgentOut>("/admin/agents", payload);
  return res.data;
}

export async function updateAgent(agentId: string, payload: AgentUpdatePayload): Promise<AgentOut> {
  const res = await apiClient.patch<AgentOut>(
    `/admin/agents/${encodeURIComponent(agentId)}`,
    payload,
  );
  return res.data;
}

export async function deleteAgent(agentId: string): Promise<void> {
  await apiClient.delete(`/admin/agents/${encodeURIComponent(agentId)}`);
}

// ────────────── 매칭 테스트 (C-2) ──────────────

export interface AgentMatchCandidate {
  id: string;
  official_name: string;
  currency: string | null;
  matched_keyword: string;
  priority: number;
}

export interface AgentMatchTestResponse {
  best_match_id: string | null;
  candidates: AgentMatchCandidate[];
}

/**
 * 임의 텍스트로 agent 매칭 시뮬레이션.
 *
 * 백엔드는 호출 시점에 ``agent_registry.load_all(db)`` 로 캐시를 강제 갱신하므로,
 * admin 이 막 추가/수정한 agent 도 즉시 반영된다.
 */
export async function matchTestAgents(text: string): Promise<AgentMatchTestResponse> {
  const res = await apiClient.post<AgentMatchTestResponse>("/admin/agents/match-test", {
    text,
  });
  return res.data;
}

// ────────────── 매칭 통계 (C-3) ──────────────

export interface AgentStatsRow {
  agent_id: string;
  official_name: string | null;
  match_count: number;
  rejected_count: number;
  first_match_at: string | null;
  last_match_at: string | null;
}

export interface AgentStatsResponse {
  total_records: number;
  unknown_count: number;
  rows: AgentStatsRow[];
  from_ts: string | null;
  to_ts: string | null;
}

export interface FetchAgentStatsParams {
  /** ISO datetime — inclusive 시작 시각. */
  from?: string | null;
  /** ISO datetime — exclusive 종료 시각. */
  to?: string | null;
}

/**
 * Agent 별 매칭 통계 조회.
 *
 * 통계는 ``Record.csv_data["agent_id"]`` 기준 GROUP BY. 새 record 가 만들어지면
 * 다음 호출에서 자동 반영되므로 캐시 staleTime 은 짧게 (기본 useQuery 가 처리).
 */
export async function fetchAgentStats(
  params: FetchAgentStatsParams = {},
): Promise<AgentStatsResponse> {
  const query: Record<string, string> = {};
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  const res = await apiClient.get<AgentStatsResponse>("/admin/agents/stats", {
    params: query,
  });
  return res.data;
}

// ────────────── 회사별 규칙 내보내기 / 가져오기 (로컬 ↔ 운영 동기화) ──────────────

export interface AgentRulesItem {
  id: string;
  official_name: string | null;
  extraction_rules: AgentExtractionRules | null;
  review_rules: AgentReviewRules | null;
}

/** 내보내기 파일 = 가져오기 입력. */
export interface AgentRulesFile {
  format: string;
  exported_at: string | null;
  source: string | null;
  agents: AgentRulesItem[];
}

export type AgentRuleField = "extraction_rules" | "review_rules";

/** 가져오면 바뀌는 회사 1곳. before/after 는 바뀌는 규칙만 담는다. */
export interface AgentRulesChange {
  agent_id: string;
  official_name: string;
  /** 파일의 회사명이 이 서버와 다를 때만 채워진다. */
  file_official_name: string | null;
  fields: AgentRuleField[];
  before: Partial<Record<AgentRuleField, Record<string, unknown> | null>>;
  after: Partial<Record<AgentRuleField, Record<string, unknown> | null>>;
}

export interface AgentRulesImportPreview {
  source: string | null;
  exported_at: string | null;
  changes: AgentRulesChange[];
  unchanged_count: number;
  unknown_ids: string[];
}

export async function exportAgentRules(): Promise<AgentRulesFile> {
  const res = await apiClient.get<AgentRulesFile>("/admin/agents/rules/export");
  return res.data;
}

export async function previewAgentRulesImport(
  file: AgentRulesFile,
): Promise<AgentRulesImportPreview> {
  const res = await apiClient.post<AgentRulesImportPreview>(
    "/admin/agents/rules/import/preview",
    file,
  );
  return res.data;
}

export async function applyAgentRulesImport(
  file: AgentRulesFile,
  agentIds: string[],
): Promise<{ applied: string[] }> {
  const res = await apiClient.post<{ applied: string[] }>("/admin/agents/rules/import", {
    file,
    agent_ids: agentIds,
  });
  return res.data;
}
